/**
 * Helper-funksjoner for kolonnetyper og business rules.
 * Bruker backend-data (viewColumnType, nullHandling, isReadOnlyEditable, hasSqlFunction)
 * i stedet for frontend-gjetting.
 */

/**
 * Henter viewColumnType fra kolonne (støtter både viewColumnType og columnType properties).
 * Backend sender både columnType og viewColumnType (lowercase i JSON).
 */
export function getViewColumnType (column: any): string | null {
  return column.viewColumnType || column.columnType || null
}

/**
 * Sjekker om kolonnen er en SystemColumn.
 */
export function isSystemColumnType (column: any): boolean {
  const type = getViewColumnType(column)
  return type === 'systemColumn' || type === 'SystemColumn'
}

/**
 * Sjekker om kolonnen er en ViewOnlyColumn.
 */
export function isViewOnlyColumnType (column: any): boolean {
  const type = getViewColumnType(column)
  return type === 'viewOnlyColumn' || type === 'ViewOnlyColumn'
}

/**
 * Sjekker om kolonnen er en TableColumn.
 */
export function isTableColumnType (column: any): boolean {
  const type = getViewColumnType(column)
  return type === 'tableColumn' || type === 'TableColumn'
}

/**
 * Sjekker om kolonnen er en SystemColumn eller ViewOnlyColumn.
 */
export function isSystemOrViewOnlyColumn (column: any): boolean {
  return isSystemColumnType(column) || isViewOnlyColumnType(column)
}

/**
 * Henter riktig verdi for includeInputFormNew basert på kolonnetype og nullHandling.
 * SystemColumn og ViewOnlyColumn skal alltid være false for ny rad.
 * Hvis nullHandling er 'required', så må kolonnen være true.
 */
export function getIncludeInputFormNewValue (column: any): boolean {
  // SystemColumn og ViewOnlyColumn skal alltid være false for ny rad
  if (isSystemOrViewOnlyColumn(column)) {
    return false
  }
  // Hvis nullHandling er 'required', så må kolonnen være true
  if (column.nullHandling === 'required') {
    return true
  }
  return column.includeInputFormNew === true
}

/**
 * Sjekker om includeInputFormNew checkbox skal være disabled.
 * SystemColumn og ViewOnlyColumn skal alltid være disabled for ny rad.
 * Hvis includeInputFormNew er null eller undefined, kan kolonnen ikke være i "ny rad".
 * Hvis nullHandling er 'required' eller 'default', så må kolonnen være disabled (alltid true).
 */
export function isIncludeInputFormNewDisabled (column: any): boolean {
  // SystemColumn og ViewOnlyColumn skal alltid være disabled for ny rad
  if (isSystemOrViewOnlyColumn(column)) {
    return true
  }
  // Hvis includeInputFormNew er null eller undefined, kan kolonnen ikke være i "ny rad"
  if (column.includeInputFormNew === null || column.includeInputFormNew === undefined) {
    return true
  }
  // Hvis nullHandling er 'required' eller 'default', så må kolonnen være disabled (alltid true)
  if (column.nullHandling === 'required' || column.nullHandling === 'default') {
    return true
  }
  return false
}

/**
 * Henter riktig verdi for isReadOnly basert på kolonnetype.
 * SystemColumn og ViewOnlyColumn skal alltid være read-only.
 */
export function getReadOnlyValue (column: any): boolean {
  // SystemColumn og ViewOnlyColumn skal alltid være read-only
  if (isSystemOrViewOnlyColumn(column)) {
    return true
  }
  // For andre kolonner, bruk den faktiske verdien
  return column.isReadOnly === true
}

/**
 * Sjekker om isReadOnly checkbox skal være disabled.
 * SystemColumn og ViewOnlyColumn skal alltid være disabled.
 * Hvis isReadOnlyEditable er false, skal den være disabled.
 * Hvis kolonnen har SQL-funksjon (beregnet felt), skal den være disabled.
 */
export function isReadOnlyDisabled (column: any): boolean {
  // SystemColumn og ViewOnlyColumn skal alltid være disabled
  if (isSystemOrViewOnlyColumn(column)) {
    return true
  }
  // Hvis isReadOnlyEditable er false, skal den være disabled
  if (column.isReadOnlyEditable === false) {
    return true
  }
  // Hvis kolonnen har SQL-funksjon (beregnet felt), skal den være disabled
  if (column.hasSqlFunction === true) {
    return true
  }
  return false
}

/**
 * Sjekker om en kolonne skal hoppes over i en operasjon.
 * @param column - Kolonne-objektet
 * @param field - Feltet som skal sjekkes (f.eks. 'showInList', 'includeInputFormNew')
 * @param includeSystem - Om systemkolonner skal inkluderes
 */
export function shouldSkipColumn (column: any, field: string, includeSystem: boolean): boolean {
  // Skip systemkolonner hvis includeSystem er false
  if (!includeSystem && isSystemColumnType(column)) {
    return true
  }
  // For includeInputFormNew, skip SystemColumn og ViewOnlyColumn alltid
  if (field === 'includeInputFormNew' && isSystemOrViewOnlyColumn(column)) {
    return true
  }
  // For includeInputFormNew, skip kolonner som ikke kan være i "ny rad" (disabled eller null/undefined)
  if (field === 'includeInputFormNew' && isIncludeInputFormNewDisabled(column)) {
    return true
  }
  return false
}

/**
 * Sjekker om alle kolonner er synlige for et gitt felt.
 * @param columns - Array av kolonner
 * @param field - Feltet som skal sjekkes
 * @param includeSystem - Om systemkolonner skal inkluderes
 */
export function areAllVisible (columns: any[], field: string, includeSystem: boolean = false): boolean {
  for (const column of columns) {
    if (shouldSkipColumn(column, field, includeSystem)) continue
    // Hvis kolonne ikke er synlig, returner false
    if (column[field] !== true) {
      return false
    }
  }
  return true
}

/**
 * Sjekker om alle kolonner er skjulte for et gitt felt.
 * @param columns - Array av kolonner
 * @param field - Feltet som skal sjekkes
 * @param includeSystem - Om systemkolonner skal inkluderes
 */
export function areAllHidden (columns: any[], field: string, includeSystem: boolean = false): boolean {
  for (const column of columns) {
    if (shouldSkipColumn(column, field, includeSystem)) continue
    // Hvis kolonne ikke er skjult, returner false
    if (column[field] !== false) {
      return false
    }
  }
  return true
}

/**
 * Sjekker om en kolonne kan vise "Ny rad" checkbox.
 * SystemColumn og ViewOnlyColumn skal ikke ha "Ny rad" checkbox.
 */
export function canShowIncludeInputFormNew (column: any): boolean {
  const columnType = getViewColumnType(column)
  return columnType !== 'systemColumn' && columnType !== 'viewOnlyColumn' && columnType !== 'SystemColumn' && columnType !== 'ViewOnlyColumn'
}

